import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Description, Storefront, Subscriptions } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import {
  DEEP,
  Eyebrow,
  GREEN,
  INK,
  LINE,
  Lede,
  MINT,
  MOCK_SHADOW,
  MUTED,
  SectionTitle,
  primaryButtonSx,
} from './marketing/MarketingKit';

/** Midpoint of typical 20–25% lead / marketplace platform fees */
const MARKETPLACE_FEE = 0.225;
/** LawnHub onboarded / own-customer platform fee */
const LAWNHUB_OWN_CUSTOMERS = 0.01;
/**
 * Top-tier lawn-care ops SaaS (CRM, scheduling, crews, payments, reporting) —
 * ballpark for full-feature team plans, not entry tier.
 */
const TOP_TIER_SAAS_MONTHLY = 499;
const ADMIN_TIME_SAVED_SHARE = 0.35;
const ROUTE_HOURS_PER_WEEK = 3;
const ROUTE_TIME_SAVED_SHARE = 0.2;
/** Average admin compensation (owner or office help) */
const ADMIN_HOURLY_VALUE = 25;

const currency = (n: number) =>
  `$${Math.max(0, Math.round(Number(n) || 0)).toLocaleString('en-US')}`;

const formatRevenueInput = (n: number) =>
  n.toLocaleString('en-US', { maximumFractionDigits: 0 });

type ComparisonRow = {
  key: string;
  icon: React.ReactNode;
  title: string;
  amount: number;
  suffix: string;
  detail: string;
  disclaimer: string;
};

type GateStep = 'locked' | 'form' | 'unlocked';

const ProviderSavingsCalculator: React.FC = () => {
  const navigate = useNavigate();
  const [monthlyRevenue, setMonthlyRevenue] = useState(20000);
  const [revenueText, setRevenueText] = useState('20,000');
  const [adminHours, setAdminHours] = useState(8);
  const [adminHoursText, setAdminHoursText] = useState('8');
  const [gate, setGate] = useState<GateStep>('locked');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const estimate = useMemo(() => {
    const annualRevenue = monthlyRevenue * 12;
    const lawnHubMonthlyFee = monthlyRevenue * LAWNHUB_OWN_CUSTOMERS;
    const lawnHubAnnualFee = lawnHubMonthlyFee * 12;

    const vsMarketplace = annualRevenue * MARKETPLACE_FEE - lawnHubAnnualFee;
    // Apples-to-apples monthly: top-tier SaaS subscription vs LawnHub 1% of monthly revenue
    const vsSaasMonthlySaved = Math.max(0, TOP_TIER_SAAS_MONTHLY - lawnHubMonthlyFee);

    const adminHoursSavedPerMonth = adminHours * ADMIN_TIME_SAVED_SHARE * 4.33;
    const routeHoursSavedPerMonth = ROUTE_HOURS_PER_WEEK * ROUTE_TIME_SAVED_SHARE * 4.33;
    const hoursSavedPerMonth = adminHoursSavedPerMonth + routeHoursSavedPerMonth;
    const hoursSavedPerYear = hoursSavedPerMonth * 12;
    const vsSpreadsheetsTimeValue = hoursSavedPerYear * ADMIN_HOURLY_VALUE;

    return {
      vsMarketplace,
      vsSaasMonthlySaved,
      lawnHubMonthlyFee,
      hoursSavedPerMonth,
      hoursSavedPerYear,
      vsSpreadsheetsTimeValue,
      lawnHubAnnualFee,
    };
  }, [monthlyRevenue, adminHours]);

  const comparisons: ComparisonRow[] = [
    {
      key: 'marketplace',
      icon: <Storefront fontSize="small" />,
      title: 'vs Lead marketplaces',
      amount: estimate.vsMarketplace,
      suffix: '/yr kept',
      detail: `~20–25% fees vs LawnHub’s 1% when you bring your own customers.`,
      disclaimer: `Calculated as (~22.5% − 1%) × your annual revenue (${currency(monthlyRevenue * 12)}). Assumes you run your own customer book on LawnHub at 1%.`,
    },
    {
      key: 'saas',
      icon: <Subscriptions fontSize="small" />,
      title: 'vs Top-tier subscription software',
      amount: estimate.vsSaasMonthlySaved,
      suffix: '/mo saved',
      detail: `Top-tier software ~${currency(TOP_TIER_SAAS_MONTHLY)}/mo vs LawnHub ~${currency(estimate.lawnHubMonthlyFee)}/mo at 1% of your monthly revenue.`,
      disclaimer: `Apples-to-apples monthly cost: ~$${TOP_TIER_SAAS_MONTHLY}/mo full-feature subscription minus LawnHub’s 1% fee on the same month’s revenue (${currency(estimate.lawnHubMonthlyFee)}/mo).`,
    },
    {
      key: 'spreadsheets',
      icon: <Description fontSize="small" />,
      title: 'vs Spreadsheets / paper',
      amount: estimate.vsSpreadsheetsTimeValue,
      suffix: '/yr time value',
      detail: `~${Math.round(estimate.hoursSavedPerMonth)} hrs/mo back (~${Math.round(estimate.hoursSavedPerYear)} hrs/yr) from less admin + smarter routing.`,
      disclaimer: `~${Math.round(estimate.hoursSavedPerYear)} hours/year × $${ADMIN_HOURLY_VALUE}/hr average admin pay (owner or office help). Hours estimate uses ~35% of your admin time plus ~20% of typical routing time.`,
    },
  ];

  const syncRevenueFromText = (raw: string) => {
    const digits = raw.replace(/[^\d]/g, '');
    setRevenueText(digits ? Number(digits).toLocaleString('en-US') : '');
    const value = digits ? parseInt(digits, 10) : 0;
    if (!Number.isNaN(value)) {
      setMonthlyRevenue(Math.min(250000, Math.max(0, value)));
    }
    if (gate === 'unlocked') setGate('locked');
  };

  const syncAdminHoursFromText = (raw: string) => {
    const digits = raw.replace(/[^\d]/g, '');
    setAdminHoursText(digits);
    const value = digits ? parseInt(digits, 10) : 0;
    if (!Number.isNaN(value)) {
      setAdminHours(Math.min(80, Math.max(0, value)));
    }
    if (gate === 'unlocked') setGate('locked');
  };

  const handleSeeSavings = () => {
    setFormError('');
    setGate('form');
  };

  const handleUnlock = async () => {
    setFormError('');
    setSubmitting(true);
    try {
      const response = await apiService.post('/leads/provider-savings', {
        email,
        phone: phone.trim() || undefined,
        monthlyRevenue,
        adminHoursPerWeek: adminHours,
        estimates: {
          vsMarketplaceAnnual: estimate.vsMarketplace,
          // Store yearly equivalent of the monthly SaaS gap for admin reporting
          vsSaasSubscriptionsAnnual: estimate.vsSaasMonthlySaved * 12,
          vsSpreadsheetsTimeValueAnnual: estimate.vsSpreadsheetsTimeValue,
          hoursSavedPerMonth: estimate.hoursSavedPerMonth,
        },
      });
      if (!response.success) {
        setFormError((response as any).error || 'Could not verify your info. Please try again.');
        return;
      }
      setGate('unlocked');
    } catch (err: any) {
      setFormError(err?.message || 'Could not verify your info. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resultsLocked = gate !== 'unlocked';

  const panelSx = {
    bgcolor: '#fff',
    border: `1px solid ${LINE}`,
    borderRadius: 3,
  } as const;

  return (
    <Box
      id="provider-savings-snapshot"
      component="section"
      sx={{
        py: { xs: 7, md: 11 },
        borderTop: `1px solid ${LINE}`,
        scrollMarginTop: { xs: 72, md: 88 },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 2, md: 6 }} sx={{ mb: { xs: 4, md: 6 } }}>
          <Grid item xs={12} md={6}>
            <Eyebrow>Free tool</Eyebrow>
            <SectionTitle>Provider Savings Snapshot</SectionTitle>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'flex-end' }}>
            <Lede>
              Enter your monthly revenue and admin hours to see how LawnHub compares with lead marketplaces,
              top-tier subscription software, and spreadsheets.
            </Lede>
          </Grid>
        </Grid>

        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={4}>
            <Box sx={{ ...panelSx, p: { xs: 2.5, md: 3 }, height: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: INK, mb: 0.75 }}>Monthly revenue</Typography>
                <TextField
                  fullWidth
                  value={revenueText}
                  onChange={(e) => syncRevenueFromText(e.target.value)}
                  onBlur={() => {
                    if (!revenueText) setRevenueText(formatRevenueInput(monthlyRevenue));
                  }}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 0.5, color: MUTED, fontWeight: 600 }}>$</Typography>,
                  }}
                  inputProps={{ inputMode: 'numeric', 'aria-label': 'Monthly revenue' }}
                />
              </Box>

              <Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: INK, mb: 0.75 }}>Hours per week on admin</Typography>
                <TextField
                  fullWidth
                  value={adminHoursText}
                  onChange={(e) => syncAdminHoursFromText(e.target.value)}
                  onBlur={() => {
                    if (!adminHoursText) setAdminHoursText(String(adminHours));
                  }}
                  helperText="Scheduling, invoicing, chasing payments"
                  inputProps={{ inputMode: 'numeric', 'aria-label': 'Admin hours per week' }}
                />
              </Box>

              {gate === 'locked' && (
                <Button variant="contained" size="large" onClick={handleSeeSavings} sx={{ ...primaryButtonSx, mt: 'auto' }}>
                  See your savings
                </Button>
              )}

              {gate === 'form' && (
                <Box sx={{ mt: 'auto' }}>
                  <Typography sx={{ fontWeight: 700, color: INK, mb: 1 }}>Unlock your snapshot</Typography>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{ mb: 1.5 }}
                    autoComplete="email"
                  />
                  <TextField
                    fullWidth
                    label="Phone (optional)"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    sx={{ mb: 1.5 }}
                    autoComplete="tel"
                  />
                  <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.5, color: MUTED, mb: 1.5 }}>
                    We’ll email you a copy of your snapshot and may follow up about LawnHub for your business.
                    We don’t sell your information (see our{' '}
                    <Box
                      component={RouterLink}
                      to="/privacy-policy"
                      target="_blank"
                      rel="noopener"
                      sx={{ color: GREEN }}
                    >
                      Privacy Policy
                    </Box>
                    ). Results also unlock on this page right away.
                  </Typography>
                  {formError && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                      {formError}
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={submitting || !email.trim()}
                    onClick={handleUnlock}
                    sx={primaryButtonSx}
                  >
                    {submitting ? <CircularProgress size={22} color="inherit" /> : 'Unlock my savings'}
                  </Button>
                </Box>
              )}

              {gate === 'unlocked' && (
                <Button variant="contained" size="large" onClick={() => navigate('/get-started')} sx={{ ...primaryButtonSx, mt: 'auto' }}>
                  Create your free account
                </Button>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{
                  ...panelSx,
                  overflow: 'hidden',
                  filter: resultsLocked ? 'blur(7px)' : 'none',
                  opacity: resultsLocked ? 0.55 : 1,
                  pointerEvents: resultsLocked ? 'none' : 'auto',
                  userSelect: resultsLocked ? 'none' : 'auto',
                  transition: 'filter 0.25s ease, opacity 0.25s ease',
                }}
              >
                {comparisons.map((row, i) => (
                  <Box
                    key={row.key}
                    sx={{
                      p: { xs: 2.25, md: 3 },
                      borderBottom: i === comparisons.length - 1 ? 'none' : `1px solid ${LINE}`,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { sm: 'flex-start' },
                      gap: { xs: 1.5, sm: 3 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        bgcolor: MINT,
                        color: GREEN,
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {row.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.05rem', color: INK, mb: 0.5 }}>
                        {row.title}
                      </Typography>
                      <Typography sx={{ fontSize: '0.925rem', color: INK, lineHeight: 1.55 }}>{row.detail}</Typography>
                      <Typography sx={{ display: 'block', mt: 1, fontSize: '0.75rem', color: MUTED, lineHeight: 1.5 }}>
                        {row.disclaimer}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, flexShrink: 0, minWidth: { sm: 140 } }}>
                      <Typography
                        sx={{
                          fontSize: { xs: '1.75rem', md: '2rem' },
                          fontWeight: 800,
                          color: DEEP,
                          letterSpacing: '-0.03em',
                          lineHeight: 1.1,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {currency(row.amount)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: MUTED }}>{row.suffix}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {resultsLocked && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
                  <Box sx={{ ...panelSx, boxShadow: MOCK_SHADOW, p: 3, maxWidth: 360, textAlign: 'center' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: INK, mb: 1 }}>
                      Your personalized savings are ready
                    </Typography>
                    <Typography sx={{ fontSize: '0.9rem', color: MUTED, lineHeight: 1.55, mb: 2 }}>
                      Enter a real email so we can follow up about fitting LawnHub to your business. We don’t sell your info.
                    </Typography>
                    {gate === 'locked' ? (
                      <Button variant="contained" onClick={handleSeeSavings} sx={primaryButtonSx}>
                        See your savings
                      </Button>
                    ) : (
                      <Stack direction="row" justifyContent="center">
                        <Typography sx={{ fontSize: '0.9rem', color: GREEN, fontWeight: 700 }}>
                          Complete the form to unlock
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
            <Typography sx={{ mt: 2.5, fontSize: '0.9rem', color: MUTED, lineHeight: 1.6, maxWidth: 640 }}>
              We built LawnHub so your business data works for you — not for us to sell. Your customers, routes, and
              numbers stay in your account. Can every tool you use today say the same?
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProviderSavingsCalculator;
